"use client";

import React, { useCallback, useEffect, useRef } from "react";
import styled, { css, createGlobalStyle } from "styled-components";
import {
  Flex,
  Text,
  utils,
  useWindowSize,
  Icon,
  useBoolean,
  Box,
  styles,
} from "@thenounproject/lingo-core";
import { buildItemUrl } from "@src/@redux/navPoint/buildItemUrl";
import InspectorDetailed from "./InspectorDetailed";
import InspectorDetailedMultiple from "./InspectorDetailedMultiple";
import InspectorSimple from "./InspectorSimple";
import EaseOutDerender from "../../../app/_components/EaseOutDerender";
import { Inspectable, InspectorSource } from "@constants/Inspector";
import { getSections } from "@src/@redux/selectors/getters";
import InspectorSimpleTags from "./InspectorSimpleTags";
import { useViewModeContext } from "@contexts/ViewModeProvider";
import useNavPoint from "@hooks/useNavPoint";
import { NavPointTypes } from "@redux/navPoint/NavPoint";
import { useAppSelectorV1 } from "@src/@redux/hooks";
import { buildLibraryAssetPath } from "@src/features/library/utils";
import InspectorSimpleMultiple from "./InspectorSimpleMultiple";
import { useInspectorBarPosition } from "./hooks/useInspectorBarPosition";
import { useGlobalShortcuts } from "@features/context-menus/ActionMenu/registry/useGlobalShortcuts";
import { useActionContextCallback } from "@features/context-menus/ActionMenu/useActionContext";

const DETAILED_INSPECTOR_WIDTH = "260px";
const TAB_WIDTH = 16;
const SMALL_BREAKPOINT = styles.bp.s;
const HOVER_THRESHOLD_PX = -8;

function getChevronIcon(belowInspectorThreshold: boolean, detailedMode: boolean): string {
  return detailedMode ? "navigation.chevron-right" : "navigation.chevron-left";
}

const IntercomStyle = createGlobalStyle`
  .intercom-launcher, .intercom-launcher-frame, .intercom-messenger, .intercom-messenger-frame {
    margin-bottom: 76px !important;
  }
`;

const InspectorViewWrapper = styled(Flex).attrs({
  height: "100%",
  width: "100%",
  flex: "1 1 auto",
  flexDirection: "column",
  overflow: "auto",
})`
  overflow-x: hidden;
  transition: padding 0.4s ease;
`;

type InspectorPanelProps = {
  isOpen: boolean;
  isOverlay?: boolean;
  panelPeak: number;
};

const InspectorPanelDetailedWrapper = styled(Flex).attrs<InspectorPanelProps>(props => ({
  height: "100%",
  borderLeft: "1px solid border",
  background: "white",
  flexDirection: "column",
  overflowX: "visible",
  flex: "1 0 auto",
  width: props.isOpen ? DETAILED_INSPECTOR_WIDTH : `${props.panelPeak}px`,
  transition: props.isOpen
    ? "width 0.3s cubic-bezier(.42,.14,.25,1)"
    : "width 0.2s cubic-bezier(.02,.22,.55,1)",
  transitionDelay: "0s",
  position: "relative",
  zIndex: "auto",
  ...(props.isOverlay && {
    transitionDelay: "0.1s",
    position: "fixed",
    zIndex: 1000,
    top: "73px",
    right: 0,
    bottom: 0,
    height: "auto",
  }),
  variations: {
    "mq.s": {
      top: "65px",
      position: "fixed",
    },
  },
}))<InspectorPanelProps>``;

const InspectorContentWrapper = styled(Flex).attrs({
  flexDirection: "column",
  height: "100%",
  width: DETAILED_INSPECTOR_WIDTH,
  transition: "opacity 0.4s ease-in",
})``;

const SimpleShownStyles = css<{ isKitContext?: boolean }>`
  bottom: 0;
  @media (max-width: 768px) {
    bottom: ${props => (props.isKitContext ? "56px" : "0")};
  }
`;

type ShownProps = {
  shown: boolean;
  isKitContext?: boolean;
};

const InspectorPanelSimpleWrapper = styled(Flex).attrs({
  height: "100px",
  alignItems: "center",
  justifyContent: "space-between",
  py: "16px",
  zIndex: "inspectorView",
})<ShownProps>`
  position: fixed;
  bottom: -100px;
  margin: 0 auto;
  transition: bottom 0.4s ease;

  ${props => props.shown && SimpleShownStyles};
`;

const InspectorSimpleInner = styled(Flex).attrs(props => {
  return {
    width: "100%",
    height: "100%",
    background: "grayLightest",
    borderRadius: utils.getBorderRadius("default", null, props),
    border: "default",
    boxShadow: "0px 4px 12px rgba(42, 42, 42, 0.1), 0px 1px 2px rgba(42, 42, 42, 0.14)",
    px: "24px",
  };
})``;

const TabButton = styled(Flex).attrs({
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  width: TAB_WIDTH,
  height: "60px",
  backgroundColor: "white",
  borderTop: "default",
  borderBottom: "default",
  borderLeft: "default",
  position: "absolute",
  variations: {
    "&:hover": {
      backgroundColor: "grayLightest",
    },
    "mq.s": {
      display: "none !important",
    },
  },
  borderRadius: "4px 0 0 4px",
})`
  position: absolute !important;
  z-index: 500;
  top: calc(50%);
  transform: translateY(-50%);
  left: ${-TAB_WIDTH}px;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;
`;

type DarkOverlayProps = {
  visible: boolean;
};

const DarkBackgroundOverlay = styled(Box).attrs<DarkOverlayProps>({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "modalOverlay",
  zIndex: 899,
})<DarkOverlayProps>`
  pointer-events: ${props => (props.visible ? "auto" : "none")};
  opacity: ${props => (props.visible ? 1 : 0)};
  transition:
    opacity 0.3s ease-in-out,
    visibility 0.3s ease-in-out;
  cursor: pointer;
`;

export type Props = {
  inspectables: Inspectable[];
  canEdit: boolean;
  children: React.ReactNode;
  source: InspectorSource;
};

const InspectorView: React.FC<Props> = ({ inspectables, canEdit, source, children }) => {
  const navPoint = useNavPoint();
  const { setInspectorMode, inspectorMode } = useViewModeContext();
  const { belowInspectorThreshold, width: windowWidth } = useWindowSize();

  // Build context callback for global shortcuts using shared hook
  const getContext = useActionContextCallback({ inspectables });

  // Enable global keyboard shortcuts for actions
  useGlobalShortcuts(getContext);

  const section = useAppSelectorV1(state => {
    if (!inspectables[0]?.item) return null;
    const sectionId = inspectables[0]?.item?.sectionId;
    const sections = getSections(state);
    return sections[`${sectionId}-${inspectables[0]?.item.version}`];
  });

  const detailedMode = inspectorMode === "detailed";
  const assetOverlayActive = navPoint && navPoint.type === "Item";
  const isKitContext = source === InspectorSource.kit;
  const isTagContext = source === InspectorSource.tags;
  const simpleMode = !detailedMode || isTagContext;

  // Hover state for peek behavior on large screens
  const [isHovered, setIsHovered, setNotHovered] = useBoolean(false);
  const inspectorPanelPeak = windowWidth <= SMALL_BREAKPOINT ? 0 : 10;
  const belowInspectorThesholdRef = useRef(belowInspectorThreshold);
  const panelRef = useRef<HTMLDivElement>(null);
  const rectRef = useRef<DOMRect | null>(null);

  // Panel can be open in two ways:
  // 1. Embedded (detailed mode on large screens)
  // 2. Overlay (hover + not pinned on large screens)
  const isPanelOpen = detailedMode || isHovered;
  const isOverlayMode = !detailedMode || (belowInspectorThreshold && detailedMode);
  const shouldRenderDetailedPanel = !isTagContext;
  const showDarkOverlay = belowInspectorThreshold && detailedMode;

  const handleTabButtonClick = useCallback(() => {
    if (!detailedMode) {
      setInspectorMode("detailed");
    } else {
      setInspectorMode("simple");
      setNotHovered();
    }
  }, [detailedMode, setInspectorMode, setNotHovered]);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!rectRef.current) return;

      const mouseX = event.clientX;
      const relativeX = mouseX - rectRef.current.left;

      if (relativeX >= HOVER_THRESHOLD_PX) {
        if (!isHovered) {
          setIsHovered();
        }
      } else {
        if (isHovered) {
          setNotHovered();
        }
      }
    },
    [isHovered, setIsHovered, setNotHovered]
  );

  const onDarkOverlayClick = useCallback(() => {
    setInspectorMode("simple");
    setNotHovered();
  }, [setInspectorMode, setNotHovered]);

  const emptyInspector = inspectables.length === 0;

  // Determine when to show the collapse button (same logic from InspectorDetailed)
  const shouldShowCollapseButton = [
    NavPointTypes.Kit,
    NavPointTypes.Gallery,
    NavPointTypes.Library,
    NavPointTypes.Section,
    NavPointTypes.KitTrashedItems,
    NavPointTypes.KitRecoveredAssets,
  ].includes(navPoint?.type);

  // Position for the simple inspector bar at the bottom
  const barPosition = useInspectorBarPosition(inspectorPanelPeak, isKitContext);

  // Cache the panel's bounding rect for performance in handleMouseMove
  useEffect(() => {
    if (panelRef.current) {
      rectRef.current = panelRef.current.getBoundingClientRect();
    }
  }, [isPanelOpen]);

  // Handle window resize: preserve inspector state by switching to overlay mode on small screens
  useEffect(() => {
    if (belowInspectorThreshold && !belowInspectorThesholdRef.current) {
      setInspectorMode("simple");
      setNotHovered();
    }
    belowInspectorThesholdRef.current = belowInspectorThreshold;
  }, [belowInspectorThreshold, setInspectorMode, setNotHovered]);

  /**
   * Render methods
   */
  function renderDetailedComponents() {
    if (isTagContext) return null;

    switch (inspectables.length) {
      case 0:
        return renderEmptyDetailedComponents();
      case 1:
        return renderSingleDetailedComponents();
      default:
        return renderMultipleDetailedComponents();
    }
  }

  function renderEmptyDetailedComponents() {
    return (
      <Flex
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        textAlign="center"
        position="relative">
        <Flex flexDirection="column" top="-xxl">
          <Text color="grayDark" px="xs">
            Select an asset to inspect
          </Text>
          <Text mt="xl" color="grayDark" px="xs">
            Shift + click to select multiple assets
          </Text>
        </Flex>
      </Flex>
    );
  }
  function renderSingleDetailedComponents() {
    if (!inspectables[0]) return;
    const [inspectable] = inspectables;

    const inspectorDetailedProps = {
      inspectable,
      source,
      canEdit,
    };
    return <InspectorDetailed {...inspectorDetailedProps} />;
  }

  function renderMultipleDetailedComponents() {
    const inspectorDetailedProps = {
      inspectables,
      canEdit,
    };
    return <InspectorDetailedMultiple {...inspectorDetailedProps} />;
  }

  function renderTagComponents() {
    return <InspectorSimpleTags {...{ inspectables }} />;
  }

  function renderSimpleComponents() {
    if (isTagContext) {
      return renderTagComponents();
    }
    switch (inspectables.length) {
      case 0:
        return null;
      case 1:
        return renderSingleSimpleComponents();
      default:
        return renderMultipleSimpleComponents();
    }
  }

  function renderSingleSimpleComponents() {
    const [inspectable] = inspectables;
    let path: string;

    if (isKitContext) {
      path = buildItemUrl(inspectable.item, {
        space: navPoint.space,
        portal: navPoint.portal,
        section,
      });
    } else {
      path = buildLibraryAssetPath(navPoint.space, inspectable.asset);
    }

    const inspectorSimpleProps = {
      inspectable,
      source,
      canEdit,
      sharePath: path,
    };
    return <InspectorSimple {...inspectorSimpleProps} />;
  }

  function renderMultipleSimpleComponents() {
    const inspectorSimpleMultipleProps = {
      inspectables,
      source,
      canEdit,
    };
    return <InspectorSimpleMultiple {...inspectorSimpleMultipleProps} />;
  }

  return (
    <Flex height="100%">
      {simpleMode && !emptyInspector && !assetOverlayActive && <IntercomStyle />}
      <InspectorViewWrapper
        id="inspector-view-wrapper"
        data-inspector-clear="true"
        data-testid="inspector-view-wrapper">
        {children}
      </InspectorViewWrapper>

      <DarkBackgroundOverlay visible={showDarkOverlay} onClick={onDarkOverlayClick} />
      {shouldRenderDetailedPanel && (
        <>
          {!detailedMode && !showDarkOverlay && !belowInspectorThreshold && (
            <Box width={inspectorPanelPeak} height="100%" />
          )}
          <InspectorPanelDetailedWrapper
            ref={panelRef}
            isOpen={isPanelOpen}
            isOverlay={isOverlayMode}
            panelPeak={inspectorPanelPeak}
            onMouseMove={!belowInspectorThreshold && !detailedMode ? handleMouseMove : undefined}
            onMouseLeave={!belowInspectorThreshold && !detailedMode ? setNotHovered : undefined}>
            {shouldShowCollapseButton && (
              <TabButton onClick={handleTabButtonClick}>
                <Icon
                  size="16px"
                  iconId={getChevronIcon(belowInspectorThreshold, detailedMode)}
                  fill="grayDarker"
                />
              </TabButton>
            )}

            <InspectorContentWrapper
              opacity={isPanelOpen ? 1 : 0}
              transitionDelay={isPanelOpen ? "0" : "0.1s"}>
              {isPanelOpen && renderDetailedComponents()}
            </InspectorContentWrapper>
          </InspectorPanelDetailedWrapper>
        </>
      )}
      <InspectorPanelSimpleWrapper
        className="inspector-simple-wrapper"
        shown={!emptyInspector && !isPanelOpen}
        isKitContext={isKitContext}
        {...barPosition}>
        <InspectorSimpleInner>
          <EaseOutDerender enabled={!isPanelOpen}>{renderSimpleComponents()}</EaseOutDerender>
        </InspectorSimpleInner>
      </InspectorPanelSimpleWrapper>
    </Flex>
  );
};

export default InspectorView;
